<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import AdaptiveDialog from '../AdaptiveDialog.vue'
import { useCoreStore, type UserPosition } from '@/stores/coreStore'
import SrDataTable from '../tables/SrDataTable.vue'
import { capitalize, isUndefined } from 'lodash'
import { i18n } from '@/i18n'
import { useOrderBookStore } from '@/stores/orderbooksStore'
import { useAutoRefresh } from '@/composable/useAutorefresh'
import { calcPriceLevel } from '@/utils'
import type { OrderBookLevels } from '@/utils/types'
import TableCards from '../TableCards.vue'
import type { TableColumn } from '../alerts/types'

const { t } = i18n.global
const props = defineProps<{
  exchanges: string[]
  symbols: string[]
}>()

const orderbookStore = useOrderBookStore()
const visible = ref(false)
const toggle = async () => {
  visible.value = !visible.value
}
defineExpose({
  toggle,
})

onMounted(() => {
  rulesRefresher.startRefresh()
})
const formattingRules = ref({})

const calcContracts = (ctx: Record<string, unknown> | undefined) => {
  if (!ctx) {
    return 0
  }
  const ctrSize = (ctx.contractSize ?? 1) as number
  return Number(ctx.contracts) * ctrSize
}

const updateFormattingRules = () => {
  formattingRules.value = {
    contracts: (v: string, ctx: Record<string, unknown> | undefined) =>
      calcContracts(ctx).toString(),
    symbol: (v: string) => {
      return v.split('/')[0] ?? ''
    },
    side: (v: string) => {
      return capitalize(t(v))
    },
    leverage: (v: string) => {
      return v ?? "-"
    },
    profileNum: (v: string) => {
      return (Number(v) + 1).toString()
    },
    liquidationPrice: (v: string) => {
      return Number(v).toString() ?? '—'
    },
    pnl: (v: string, ctx: Record<string, unknown> | undefined) => {
      if (!ctx || !ctx.entryPrice) {
        return '—'
      }
      const symbol = ctx.symbol as string
      const asset = symbol.split('/')[0] as string
      const exchange = ctx.exchange as string
      const ob = orderbookStore.get(exchange, 'swap', asset)
      const isLong = ctx.side == 'long'
      const obSide = (isLong ? ob?.bids : ob?.asks) as OrderBookLevels
      const entry = Number(ctx.entryPrice)

      const ctrs = calcContracts(ctx)
      if (!ctrs) {
        return '—'
      }

      const levels = calcPriceLevel(obSide, ctrs * entry, 1)
      if (!levels) {
        return '—'
      }
      const [price, collectedVol] = levels
      if (!price || !collectedVol) {
        return '—'
      }

      const output = isLong ? (price - entry) * ctrs : (entry - price) * ctrs

      return `${output.toFixed(3)}$`
    },
  }
}

const rulesRefresher = useAutoRefresh(async () => {
  updateFormattingRules()
}, 1000)

const coreStore = useCoreStore()
const columns: TableColumn[] = [
  {
    field: 'profileNum',
    header: 'profileNum',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as UserPosition
      const sideClass = data.side == "long" ? "success" : "danger"
      return {
        class: `pit digit ${sideClass}`,
      }
    },
  },
  {
    field: 'symbol',
    header: 'symbol',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as UserPosition
      const sideClass = data.side == "long" ? "success" : "danger"
      return {
        class: `pit ${sideClass}`,
      }
    },
  },
  {
    field: 'side',
    header: 'side',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as UserPosition
      const sideClass = data.side == "long" ? "success" : "danger"
      return {
        class: `pit ${sideClass}`,
      }
    },
  },
  {
    field: 'leverage',
    header: 'leverage',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as UserPosition
      const sideClass = data.side == "long" ? "success" : "danger"
      return {
        class: `pit digit ${sideClass}`,
      }
    },
  },
  {
    field: 'contracts',
    header: 'amount',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as UserPosition
      const sideClass = data.side == "long" ? "success" : "danger"
      return {
        class: `pit digit ${sideClass}`,
      }
    },
  },
  {
    field: 'entryPrice',
    header: 'entryPrice',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as UserPosition
      const sideClass = data.side == "long" ? "success" : "danger"
      return {
        class: `pit digit ${sideClass}`,
      }
    },
  },
  {
    field: 'liquidationPrice',
    header: 'liquidationPrice',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as UserPosition
      const sideClass = data.side == "long" ? "success" : "danger"
      return {
        class: `pit digit ${sideClass}`,
      }
    },
  },
  {
    field: 'pnl',
    header: 'pnl',
    class: 'text-center',
    inner: 'a',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as UserPosition
      const sideClass = data.side == "long" ? "success" : "danger"
      return {
        class: `pit digit ${sideClass}`,
      }
    },
  },
]

const userPositions = computed(() => {
  return coreStore.parsePositions(props.exchanges, props.symbols)
})

</script>

<template>
  <AdaptiveDialog class="mt-10 lg:mt-20" v-model:visible="visible" pt:root="w-350" pt:content="p-0"
    pt:header="px-4 py-1" :header="$t('modal.userProfilesInfo.title')">
    <div class="block lg:hidden">
      <TableCards :values="userPositions" :columns="columns" :formattingRules="formattingRules">
      </TableCards>
    </div>
    <div class="hidden lg:block">
      <SrDataTable v-if="!isUndefined(userPositions)" size="small" :columns="columns" :value="userPositions"
        :formattingRules="formattingRules">
        <template #empty>
          <div class="text-center">
            {{ $t('noPositionsFound') }}
          </div>
        </template>
      </SrDataTable>
    </div>
  </AdaptiveDialog>
</template>
